import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useRef } from 'react';

import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { sportProfile } from '@sorare/core/src/contexts/currentUser/queries';
import { useDialogOpenedContext } from '@sorare/core/src/contexts/dialogOpened';
import { useSnackNotificationContext } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags.tsx';
import type {
  claimInteruptedCardsFromPackMutation,
  claimInteruptedCardsFromPackMutationVariables,
} from './__generated__/usePackOpeningInterruptedSnackbar.graphql.ts';

const CLAIM_INTERUPTED_CARDS_FROM_PACK_MUTATION = gql`
  mutation claimInteruptedCardsFromPackMutation(
    $input: claimCardsFromPackInput!
    $sport: Sport!
  ) {
    claimCardsFromPack(input: $input) {
      currentUser {
        slug
        claimablePacks(sport: $sport) {
          id
        }
        ...CurrentUseProvider_sportProfile
      }
      errors {
        message
        code
      }
    }
  }
  ${sportProfile}
` as TypedDocumentNode<
  claimInteruptedCardsFromPackMutation,
  claimInteruptedCardsFromPackMutationVariables
>;

export const usePackOpeningInterruptedSnackbar = () => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  const { isIosApp } = useIsMobileApp();
  const {
    flags: { disableAutoClaimPackOpeningInterrupted = false },
  } = useFeatureFlags();

  const { showNotification } = useSnackNotificationContext();

  const [claimInteruptedCardsFromPack] = useMutation(
    CLAIM_INTERUPTED_CARDS_FROM_PACK_MUTATION
  );
  const requestedPackIds = useRef<string[]>([]);
  const { isDialogOpen } = useDialogOpenedContext();

  const shouldClaim =
    !isIosApp && currentUser?.claimablePacks.length && !isDialogOpen;

  useEffect(() => {
    if (!shouldClaim || disableAutoClaimPackOpeningInterrupted) {
      return;
    }
    if (currentUser?.claimablePacks.length) {
      (async () => {
        await Promise.all(
          currentUser.claimablePacks.map(async pack => {
            if (requestedPackIds.current.includes(pack.id)) {
              return;
            }
            requestedPackIds.current.push(pack.id);
            await claimInteruptedCardsFromPack({
              variables: {
                sport,
                input: {
                  packId: pack.id,
                  chosenCardSlugs: pack.cards.map(card => card.slug),
                },
              },
            });
          })
        );
        showNotification('packOpeningInterrupted');
      })();
    }
  }, [
    currentUser?.claimablePacks,
    claimInteruptedCardsFromPack,
    shouldClaim,
    showNotification,
    sport,
    disableAutoClaimPackOpeningInterrupted,
  ]);

  return null;
};
